/**
 * paymentPosture.ts — the portal's half of the card-on-file ladder: the two
 * questions every consumer of `dealCard.paymentPosture` has to ask, answered in
 * ONE place.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY A MODULE AND NOT A COMPARISON AT EACH CALL SITE. Three surfaces branch on
 * this fact and they must not drift: the Patient journey panel's action band
 * (what does this patient need next), the rail's payment rungs, and whatever
 * routing rule follows. The clinic sibling names the same set for the same
 * reason (`deal-payment-posture.ts` → `CHARGEABLE_POSTURES`), so the two
 * services and this portal ask ONE question and get ONE answer.
 *
 * ⚠️ NOT IN `api/types.ts`. That module is types only — dozens of call sites
 * import from it with `import type`, which erases at compile time and would take
 * a runtime const with it. The shape lives there; the judgements live here.
 *
 * MONEY-FREE BY CONSTRUCTION: nothing in this file reads a pence field. The
 * role-shaped money gating belongs to the rendering surface, which has the role.
 */
import type { DealPaymentPosture, DealPaymentPostureState } from '../api/types';

/**
 * The rungs from which money can move without sending the patient anywhere.
 *
 * ⚠️ ONLY `authorised` and `captured`. `card_on_file` is deliberately OUT and
 * this is the single most damaging mistake a consumer of the contract can make:
 * a saved card is a payment METHOD, not permission for this plan's money.
 * `OrdersService.approve()` requires a `month1_preauth` in `held`/`captured` and
 * throws on every lower rung, so the two adjacent rungs decide whether a
 * clinician's approval takes the money or 409s in their face.
 */
export const CHARGEABLE_POSTURES: readonly DealPaymentPostureState[] = ['authorised', 'captured'];

/**
 * True when the money for this plan is already committed.
 *
 * ⚠️ `unknown` ANSWERS FALSE, and that is the fail-safe direction, not an
 * oversight: it routes a patient to a link we may not have needed rather than
 * promising a charge that will throw. It is still NOT "no card" — never render
 * it as one; see `postureIsKnown`.
 */
export function isChargeablePosture(state: DealPaymentPostureState): boolean {
  return CHARGEABLE_POSTURES.includes(state);
}

/**
 * ORDER STATES WITH NO MONEY LEFT TO MOVE — the portal mirror of the sibling's
 * `TERMINAL_ORDER_STATES`.
 *
 * ⚠️ THE LADDER DOES NOT KNOW ABOUT THESE. A rejected or cancelled case whose
 * card is still on file reads `card_on_file` exactly like a live one, because
 * the ladder describes the PAYMENT and this describes the ORDER. Rule 5 of the
 * seam contract: `orderState` is context, not the routing fact — but a terminal
 * order must never be routed as chargeable however high the rung.
 *
 * A plain string set rather than a pinned union, for the same tolerance reason
 * `orderState` itself is a plain string: an orders deploy that names a state
 * this portal has never heard of must not fail a board render. An unknown state
 * is NOT terminal here, which keeps the default "there may still be work".
 */
export const TERMINAL_ORDER_STATES: readonly string[] = ['rejected', 'cancelled'];

/** True when the order behind a posture has ended — see the set above. */
export function isTerminalOrderState(orderState: string | null | undefined): boolean {
  return orderState != null && TERMINAL_ORDER_STATES.includes(orderState);
}

/**
 * ORDER STATES IN WHICH THE PHARMACY STILL HAS WORK — the portal mirror of
 * orders-service's `PHARMACY_WORK_STATES` (2026-09-11): `awaiting_dispense` (a
 * dispense or a refusal is owed) and `dispensed` (the courier and tracking are
 * owed). `dispatched` is out — that month is done, and a repeat month puts the
 * SAME order back to `awaiting_dispense` on its own.
 *
 * ⚠️ FAILS CLOSED, the opposite of `TERMINAL_ORDER_STATES` above: a null or
 * unknown state is NOT pharmacy work. This set gates handing out a pharmacy
 * sign-in link — a credential — so "we don't know" must mean "don't offer it".
 * The server refuses on the same set; this only keeps the button off where it
 * would be refused.
 */
export const PHARMACY_WORK_ORDER_STATES: readonly string[] = ['awaiting_dispense', 'dispensed'];

/** True while the pharmacy still owes a dispense, a refusal, or the dispatch. */
export function orderHasPharmacyWork(orderState: string | null | undefined): boolean {
  return orderState != null && PHARMACY_WORK_ORDER_STATES.includes(orderState);
}

/**
 * ORDER STATES THAT PROVE THE DISPENSARY WAS TOLD (2026-09-11, incident
 * 2026-09-09) — the states an order can only be in because a pharmacy send
 * CONFIRMED.
 *
 * ⚠️ WHY THIS EXISTS. A capture and a pharmacy send are separate events.
 * orders-service banks the money, then issues the prescription and posts it to
 * the dispensary POST-COMMIT and best-effort, so that nothing in that tail can
 * unwind a charge — which means a capture can succeed with the send failing
 * behind it. `recordPharmacySendWithinTx` flips `approved → awaiting_dispense`
 * ONLY when `PharmacySend.sentAt` is non-null, so an order still sitting at
 * `approved` under a `captured` posture is a paid order the pharmacy has never
 * heard of. That is not a transient: the send is attempted once, in the same
 * post-commit phase as the capture, so the window is milliseconds and anything
 * sustained is a stall.
 *
 * On 2026-09-09 one lasted two days. A guest patient has no identity `Patient`
 * row, so the dispensing request had no name and no date of birth and
 * `sendDispenseRequest` refused it — correctly. The drawer's band read
 * "Next: the pharmacy dispenses and dispatches. Nothing for us to do." in
 * green, with no owner, because it asked only whether the money had moved.
 *
 * ⚠️ `dispense_rejected` IS IN THE SET, and leaving it out would replace one
 * false sentence with another: that pharmacy DID receive the prescription and
 * refused to dispense it. That is the rail's `halt` case and somebody else's
 * problem to state — it is emphatically not "never sent".
 *
 * ⚠️ FAILS CLOSED like `orderHasPharmacyWork`, and here that means the opposite
 * direction of caution: a null or unknown state has NOT reached the pharmacy, so
 * the surface under-claims and asks a human to look. A wrongly-raised flag costs
 * somebody opening an order that turns out to be fine; a wrongly-lowered one
 * cost a real patient two days of her treatment.
 */
export const PHARMACY_REACHED_ORDER_STATES: readonly string[] = [
  'awaiting_dispense',
  'dispensed',
  'dispatched',
  'dispense_rejected',
];

/** True when the dispensary has demonstrably been sent this order's prescription. */
export function orderReachedPharmacy(orderState: string | null | undefined): boolean {
  return orderState != null && PHARMACY_REACHED_ORDER_STATES.includes(orderState);
}

/**
 * DID WE ACTUALLY LEARN ANYTHING? The one guard that separates the third
 * absence from the other two.
 *
 * ⚠️ THREE DIFFERENT ABSENCES AND THEY ARE NOT INTERCHANGEABLE:
 *   `undefined`            a clinic-service that predates the seam.
 *   `null`                 this RESPONSE did not ask (every mutation echo).
 *   `{state:'unknown'}`    we asked and orders-service could not answer.
 * The first two mean "we have no reading"; the third means "the reading is that
 * we do not know". All three answer false here, and NONE of them may be rendered
 * or routed as `no_order` — "there is no order" is a claim about a patient's
 * money, and we are not entitled to make it from an absence.
 */
export function postureIsKnown(
  posture: DealPaymentPosture | null | undefined,
): posture is DealPaymentPosture {
  return posture != null && posture.state !== 'unknown';
}

/**
 * How far up the ladder a posture stands, for "is this rung reached?" questions.
 *
 * ⚠️ `unknown` IS NOT ON THE LADDER and is deliberately absent from this map:
 * asking how far up a transport failure stands is a category error, and giving
 * it a number would let a `>=` comparison quietly treat "we could not ask" as a
 * position. Callers must gate on `postureIsKnown` first — `postureRank` returns
 * `null` for it, so a careless `>=` is a type error rather than a wrong answer.
 */
const POSTURE_RANK: Record<Exclude<DealPaymentPostureState, 'unknown'>, number> = {
  no_order: 0,
  plan_only: 1,
  checkout_started: 2,
  card_on_file: 3,
  authorised: 4,
  captured: 5,
};

/** The rung's height, or null for `unknown` — which is not a rung. */
export function postureRank(state: DealPaymentPostureState): number | null {
  return state === 'unknown' ? null : POSTURE_RANK[state];
}

/**
 * Has the posture reached AT LEAST this rung? False for `unknown`, for the same
 * fail-safe reason `isChargeablePosture` is.
 */
export function postureReached(
  posture: DealPaymentPosture | null | undefined,
  rung: Exclude<DealPaymentPostureState, 'unknown'>,
): boolean {
  if (!postureIsKnown(posture)) return false;
  const here = postureRank(posture.state);
  return here !== null && here >= POSTURE_RANK[rung];
}
